import { z } from "zod";
import { paginationSchema } from "./common.js";

export const payPeriodStatusSchema = z.enum([
  "OPEN",
  "IN_REVIEW",
  "APPROVED",
  "LOCKED",
  "EXPORTED",
]);

export const payPeriodTypeSchema = z.enum(["WEEKLY", "BIWEEKLY", "SEMIMONTHLY", "MONTHLY"]);

export const createPayPeriodSchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  autoCreateNext: z.boolean().optional().default(false),
});

export const payPeriodListQuerySchema = paginationSchema.extend({
  status: payPeriodStatusSchema.optional(),
});

export const payPeriodSummarySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  label: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: payPeriodStatusSchema,
  totalWorkers: z.number().int(),
  grossPay: z.number().nullable(),
  openExceptions: z.number().int(),
  approvedAt: z.string().nullable(),
  lockedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const payPeriodBreakdownRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumPay: z.number(),
  equipmentCost: z.number(),
  grossPay: z.number(),
});

export const payPeriodExceptionSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  severity: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]),
  message: z.string(),
  targetType: z.string(),
  targetId: z.string().uuid(),
  status: z.enum(["OPEN", "RESOLVED", "IGNORED"]),
});

export const payPeriodAuditActivitySchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
  summary: z.string().nullable(),
  reason: z.string().nullable(),
});

export const payPeriodTotalsSchema = z.object({
  totalWorkers: z.number().int(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumPay: z.number(),
  equipmentCost: z.number(),
  grossPay: z.number(),
  openExceptions: z.number().int(),
  criticalExceptions: z.number().int(),
});

export const payPeriodDetailSchema = payPeriodSummarySchema.extend({
  approvedById: z.string().uuid().nullable(),
  approvedByName: z.string().nullable(),
  totals: payPeriodTotalsSchema,
  siteBreakdown: z.array(payPeriodBreakdownRowSchema),
  workerBreakdown: z.array(payPeriodBreakdownRowSchema),
  equipmentBreakdown: z.array(payPeriodBreakdownRowSchema),
  exceptions: z.array(payPeriodExceptionSchema),
  auditActivity: z.array(payPeriodAuditActivitySchema),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
  canLock: z.boolean(),
  canUnlock: z.boolean(),
  canExport: z.boolean(),
  canRecalculate: z.boolean(),
});

export const payPeriodReviewEntrySchema = z.object({
  id: z.string().uuid(),
  date: z.string(),
  workerId: z.string().uuid(),
  workerName: z.string(),
  siteId: z.string().uuid(),
  siteName: z.string(),
  siteCode: z.string(),
  hours: z.number(),
  status: z.string(),
  grossPay: z.number().nullable(),
  regularHours: z.number().nullable(),
  overtimeHours: z.number().nullable(),
  doubleTimeHours: z.number().nullable(),
});

export const payPeriodReviewWorkerStatusSchema = z.enum([
  "COMPLETE",
  "PENDING",
  "NEEDS_CORRECTION",
  "INCOMPLETE",
  "EXCEPTION",
]);

export const payPeriodReviewWorkerSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string(),
  name: z.string(),
  siteId: z.string().uuid(),
  siteCode: z.string(),
  siteName: z.string(),
  crew: z.string().nullable(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumPay: z.number(),
  grossPay: z.number(),
  equipmentCost: z.number(),
  entryCount: z.number().int(),
  status: payPeriodReviewWorkerStatusSchema,
  openExceptions: z.number().int(),
  canApprove: z.boolean(),
  canRequestCorrection: z.boolean(),
});

export const payPeriodReviewSiteSchema = payPeriodBreakdownRowSchema.extend({
  workerCount: z.number().int(),
  openExceptions: z.number().int(),
  status: z.enum(["APPROVED", "PENDING", "EXCEPTION"]),
  canApprove: z.boolean(),
});

export const payPeriodReviewCrewSchema = z.object({
  name: z.string(),
  workerCount: z.number().int(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumPay: z.number(),
  equipmentCost: z.number(),
  grossPay: z.number(),
  openExceptions: z.number().int(),
});

export const payPeriodReviewEquipmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
  usageHours: z.number(),
  workerCount: z.number().int(),
  equipmentCost: z.number(),
});

export const payPeriodApprovalProgressSchema = z.object({
  workersApproved: z.number().int(),
  workersTotal: z.number().int(),
  sitesApproved: z.number().int(),
  sitesTotal: z.number().int(),
  entriesApproved: z.number().int(),
  entriesTotal: z.number().int(),
  percentComplete: z.number(),
});

export const payPeriodIntegritySchema = z.object({
  allEntriesHaveSource: z.boolean(),
  noRuleConflicts: z.boolean(),
  exceptionsReviewed: z.boolean(),
  approvalsPending: z.number().int(),
  onchainProofStatus: z.enum(["NONE", "PENDING", "PUBLISHED", "FAILED"]).nullable(),
  merkleRootPreview: z.string().nullable(),
  onchainProofId: z.string().uuid().nullable().optional(),
  payPeriodPublicRef: z.string().nullable().optional(),
  transactionSignaturePreview: z.string().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
});

export const requestCorrectionSchema = z.object({
  reason: z.string().trim().min(1, "Reason is required").max(500),
});

export const payPeriodWorkerParamSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
});

export const payPeriodSiteParamSchema = z.object({
  id: z.string().uuid(),
  siteId: z.string().uuid(),
});

export const payPeriodReviewQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  siteId: z.string().uuid().optional(),
  crew: z.string().trim().max(100).optional(),
  workerStatus: payPeriodReviewWorkerStatusSchema.optional(),
});

export const payPeriodReviewSchema = z.object({
  payPeriodId: z.string().uuid(),
  label: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: payPeriodStatusSchema,
  totals: payPeriodTotalsSchema,
  approvalProgress: payPeriodApprovalProgressSchema,
  integrity: payPeriodIntegritySchema,
  workers: z.array(payPeriodReviewWorkerSchema),
  workersTotal: z.number().int().optional(),
  sites: z.array(payPeriodReviewSiteSchema),
  crews: z.array(payPeriodReviewCrewSchema),
  equipment: z.array(payPeriodReviewEquipmentSchema),
  exceptions: z.array(payPeriodExceptionSchema),
  auditActivity: z.array(payPeriodAuditActivitySchema),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
  canLock: z.boolean(),
  canExport: z.boolean(),
  canRecalculate: z.boolean(),
});

export const unlockPayPeriodSchema = z.object({
  reason: z.string().trim().min(1, "Reason is required").max(500),
});

export const exportPayPeriodSchema = z.object({
  type: z.enum(["CSV", "PDF", "PAYROLL"]).default("PAYROLL"),
});

export const exportPayPeriodResponseSchema = z.object({
  payPeriodId: z.string().uuid(),
  exportBatchId: z.string().uuid(),
  status: z.string(),
  queued: z.boolean(),
});

export type PayPeriodReviewQuery = z.infer<typeof payPeriodReviewQuerySchema>;
export type PayPeriodStatus = z.infer<typeof payPeriodStatusSchema>;
export type CreatePayPeriodInput = z.infer<typeof createPayPeriodSchema>;
export type PayPeriodSummary = z.infer<typeof payPeriodSummarySchema>;
export type PayPeriodDetail = z.infer<typeof payPeriodDetailSchema>;
export type PayPeriodReview = z.infer<typeof payPeriodReviewSchema>;
export type PayPeriodReviewWorker = z.infer<typeof payPeriodReviewWorkerSchema>;
export type RequestCorrectionInput = z.infer<typeof requestCorrectionSchema>;
export type UnlockPayPeriodInput = z.infer<typeof unlockPayPeriodSchema>;
export type ExportPayPeriodInput = z.infer<typeof exportPayPeriodSchema>;
export type ExportPayPeriodResponse = z.infer<typeof exportPayPeriodResponseSchema>;
